import { AlertCircle, AlertTriangle } from 'lucide-react'
import { useNavigate } from 'react-router'
import { StatusBadge } from '@/components/ui/status-badge'
import { formatNumber, formatPercent } from '@/lib/format'
import type { OverageAccount } from '@/lib/types'

interface OveragePanelsProps {
  approaching: OverageAccount[]
  inOverage: OverageAccount[]
}

export function OveragePanels({ approaching, inOverage }: OveragePanelsProps): React.JSX.Element {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <OveragePanel
        title="Approaching Limits"
        subtitle="Accounts above 80% of quota"
        icon={<AlertTriangle className="h-4 w-4 text-yellow-500" />}
        accounts={approaching}
        borderClass="border-yellow-200"
        emptyMessage="No accounts approaching their limits."
      />
      <OveragePanel
        title="In Overage"
        subtitle="Accounts exceeding their quota"
        icon={<AlertCircle className="h-4 w-4 text-red-500" />}
        accounts={inOverage}
        borderClass="border-red-200"
        emptyMessage="No accounts currently in overage."
        showOverageCount
      />
    </div>
  )
}

interface OveragePanelProps {
  title: string
  subtitle: string
  icon: React.ReactNode
  accounts: OverageAccount[]
  borderClass: string
  emptyMessage: string
  showOverageCount?: boolean
}

function OveragePanel({
  title,
  subtitle,
  icon,
  accounts,
  borderClass,
  emptyMessage,
  showOverageCount = false,
}: OveragePanelProps): React.JSX.Element {
  const navigate = useNavigate()

  return (
    <div className={`rounded-xl border bg-surface-0 p-6 ${borderClass}`}>
      <div className="mb-4 flex items-center gap-2">
        {icon}
        <div>
          <h3 className="text-sm font-medium text-text-primary">{title}</h3>
          <p className="text-xs text-text-tertiary">{subtitle}</p>
        </div>
      </div>

      {accounts.length === 0 ? (
        <p className="py-4 text-center text-sm text-text-tertiary">{emptyMessage}</p>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="flex items-center justify-between rounded-lg border border-surface-3 p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-primary">
                  {account.user_email}
                </p>
                <div className="mt-0.5 flex items-center gap-2">
                  <StatusBadge status={account.tier} />
                  <span className="text-xs text-text-tertiary">
                    {formatNumber(account.emails_sent)} / {formatNumber(account.limit)} (
                    {formatPercent(account.percent_used)})
                  </span>
                  {showOverageCount && account.overage_count > 0 && (
                    <span
                      className={`text-xs font-medium ${account.overage_count >= 4 ? 'text-red-600' : 'text-yellow-600'}`}
                    >
                      Overage #{account.overage_count}
                      {account.overage_count >= 4 ? ' (hard-block)' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-3 flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/accounts/${account.id}`)}
                  className="rounded-lg border border-surface-3 px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-1"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
